import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Send, CheckCircle2, Loader2, MessageSquare, Target, AlertTriangle, Calendar, Plus, Trash2, Pencil, Clock, Save, Star,
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { StarRating } from './components/shared';
import { usePermission } from '@/contexts/RoleAccessContext';
import { usePerformanceBase, useReviewsListPath } from '@/contexts/TeamScopeContext';
import { createCanHelpers } from '@/utils/rbac';

import { getEmployees } from '@/services/adminService';
import { getTemplates } from '@/services/templateService';
import { createPIP, getPIPDetails, savePIP } from '@/services/performanceService';

// Auto-generate milestones based on start/end dates
const generateMilestones = (startDate, endDate) => {
  if (!startDate || !endDate) return [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return [];

  const milestones = [];
  const addDate = (d, days) => {
    const nd = new Date(d);
    nd.setDate(nd.getDate() + days);
    return nd.toISOString().split('T')[0];
  };

  if (diffDays <= 14) {
    milestones.push({ milestone_id: 'm1', id: 'm1', label: 'Mid-Point Check-in', date: addDate(start, Math.round(diffDays / 2)), completed: false, status: 'Pending', notes: '' });
    milestones.push({ milestone_id: 'm2', id: 'm2', label: 'Final Review', date: endDate, completed: false, status: 'Pending', notes: '' });
  } else if (diffDays <= 30) {
    milestones.push({ milestone_id: 'm1', id: 'm1', label: 'Week 1 Check-in', date: addDate(start, 7), completed: false, status: 'Pending', notes: '' });
    milestones.push({ milestone_id: 'm2', id: 'm2', label: 'Mid-Point Review', date: addDate(start, Math.round(diffDays / 2)), completed: false, status: 'Pending', notes: '' });
    milestones.push({ milestone_id: 'm3', id: 'm3', label: 'Final Review', date: endDate, completed: false, status: 'Pending', notes: '' });
  } else if (diffDays <= 60) {
    milestones.push({ milestone_id: 'm1', id: 'm1', label: 'Week 1 Check-in', date: addDate(start, 7), completed: false, status: 'Pending', notes: '' });
    milestones.push({ milestone_id: 'm2', id: 'm2', label: 'Week 2 Check-in', date: addDate(start, 14), completed: false, status: 'Pending', notes: '' });
    milestones.push({ milestone_id: 'm3', id: 'm3', label: 'Mid-Point Review', date: addDate(start, Math.round(diffDays / 2)), completed: false, status: 'Pending', notes: '' });
    milestones.push({ milestone_id: 'm4', id: 'm4', label: 'Final Review', date: endDate, completed: false, status: 'Pending', notes: '' });
  } else {
    const interval = Math.round(diffDays / 5);
    for (let i = 1; i <= 4; i++) {
      milestones.push({ milestone_id: `m${i}`, id: `m${i}`, label: `Review ${i}`, date: addDate(start, interval * i), completed: false, status: 'Pending', notes: '' });
    }
    milestones.push({ milestone_id: 'm5', id: 'm5', label: 'Final Review', date: endDate, completed: false, status: 'Pending', notes: '' });
  }
  return milestones;
};

const PIPForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';
  const employeeIdParam = searchParams.get('employeeId');

  const { can } = usePermission();
  const performanceBase = usePerformanceBase();
  const reviewsPath = useReviewsListPath();
  const { canView, canCreate, canEdit, canDelete, canApprove } = createCanHelpers(can);

  const [pip, setPip] = useState(null);
  const [template, setTemplate] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Creation state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(employeeIdParam || '');
  const [reason, setReason] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [creating, setCreating] = useState(false);

  // Form state for template questions
  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState({});
  const [starScores, setStarScores] = useState({});

  // Milestone check-in dialog
  const [checkInMilestone, setCheckInMilestone] = useState(null);
  const [checkInNotes, setCheckInNotes] = useState('');
  const [checkInRating, setCheckInRating] = useState('');
  const [savingCheckIn, setSavingCheckIn] = useState(false);

  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);

  const isFormComplete = () => {
    const allQuestionsAnswered = allQuestionIds.every(qId => ratings[qId]);

    const pipMilestones = pip?.pip_milestones || [];
    const allMilestonesCompleted = pipMilestones.length > 0 &&
      pipMilestones.every(m => m.completed === true || m.status === 'Completed');

    return allQuestionsAnswered && allMilestonesCompleted;
  };

  const handleCompletePIPClick = () => {
    if (isFormComplete()) {
      setShowCompleteConfirm(true);
    }
  };

  const confirmCompletePIP = async () => {
    setShowCompleteConfirm(false);
    await handleCompletePIP();
  };

  useEffect(() => {
    if (isNew) loadCreationData();
    else loadPIP();
  }, [id]);

  // Auto-generate milestones when dates change
  useEffect(() => {
    if (isNew && startDate && endDate) {
      setMilestones(generateMilestones(startDate, endDate));
    }
  }, [startDate, endDate, isNew]);

  const loadCreationData = async () => {
    try {
      // Same scoped list as the other performance pickers — managers only get their direct reports.
      const [tmplResponse, empList] = await Promise.all([
        getTemplates(),
        getEmployees(undefined, undefined, { statuses: ["active", "exit_scheduled"], scope: "performance" }),
      ]);
      const tmplList = Array.isArray(tmplResponse) ? tmplResponse : (tmplResponse?.data || []);
      setTemplates(tmplList.filter(t => t.type === 'PIP'));
      setEmployees(empList);

      if (employeeIdParam) {
        const emp = empList.find(e => e.employee_id === employeeIdParam);
        setEmployee(emp || null);
      }
    } catch (error) {
      console.error("Failed to load data : ", error);
      toast.error("Failed to load data");
    }
    finally { setLoading(false); }
  };

  const loadPIP = async () => {
    try {
      const pipResponse = await getPIPDetails(id);
      const pipData = pipResponse.data;

      setPip(pipData);

      if (pipData.template_id) {
        if (typeof pipData.template_id === 'object' && pipData.template_id !== null) {
          setTemplate(pipData.template_id);
        } else {
          const templateResponse = await getTemplates();
          const templatesArray = Array.isArray(templateResponse) ? templateResponse : (templateResponse?.data || []);
          const tmpl = templatesArray.find(t => t._id === pipData.template_id);
          setTemplate(tmpl || null);
        }
      }

      if (pipData.employee_id) {
        if (typeof pipData.employee_id === 'object' && pipData.employee_id !== null) {
          setEmployee(pipData.employee_id);
        } else {
          const employeesList = await getEmployees();
          const emp = employeesList.find(e =>
            e.employee_id === pipData.employee_id || e._id === pipData.employee_id
          );
          setEmployee(emp || null);
        }
      }

      if (pipData.manager_review && pipData.manager_review.responses) {
        const loadedRatings = {};
        const loadedComments = {};
        const loadedStarScores = {};

        pipData.manager_review.responses.forEach(r => {
          loadedRatings[r.question_id] = r.rating_label || r.rating;
          loadedComments[r.question_id] = r.comment || '';
          if (r.star_score && r.star_score > 0) {
            loadedStarScores[r.question_id] = r.star_score;
          } else if (r.rating_label) {
            const optionIndex = ratingOptions.indexOf(r.rating_label);
            loadedStarScores[r.question_id] = optionIndex >= 0 ? optionIndex + 1 : 0;
          } else {
            loadedStarScores[r.question_id] = 0;
          }
        });

        setRatings(loadedRatings);
        setComments(loadedComments);
        setStarScores(loadedStarScores);
      }

    } catch (error) {
      console.error("Failed to load PIP:", error);
      toast.error("Failed to load PIP");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedTemplateId) { toast.error("Please select a template"); return; }
    if (!selectedEmployeeId) { toast.error("Please select an employee"); return; }
    if (!startDate || !endDate) { toast.error("Please set start and end dates"); return; }

    setCreating(true);
    try {
      const response = await createPIP(
        selectedEmployeeId,
        selectedTemplateId,
        reason,
        startDate,
        endDate,
        milestones
      );

      toast.success(response.message || "PIP created successfully!");

      const pipId = response.data._id || response.data.id;
      navigate(`${performanceBase}/pip/${pipId}`, { replace: true });

    } catch (error) {
      console.error("Failed to create PIP:", error);
      toast.error(error.response?.data?.message || "Failed to create PIP");
    } finally {
      setCreating(false);
    }
  };

  // Helper function to get milestone ID regardless of field name
  const getMilestoneId = (m) => m?.milestone_id || m?.id || m?._id;

  const handleMilestoneCheckIn = async () => {
    if (!checkInMilestone) return;
    if (!canManagePIP) {
      toast.error("Only the assigned manager can update this PIP");
      return;
    }
    setSavingCheckIn(true);
    try {
      const currentMilestones = pip.pip_milestones || [];
      const updatedMilestones = currentMilestones.map(m => {
        if (getMilestoneId(m) === getMilestoneId(checkInMilestone)) {
          return {
            ...m,
            completed: true,
            status: 'Completed',
            notes: checkInNotes,
            rating: checkInRating,
            completed_at: new Date().toISOString()
          };
        }
        return m;
      });

      await savePIP(id, { milestones: updatedMilestones });

      setCheckInMilestone(null);
      setCheckInNotes('');
      setCheckInRating('');
      toast.success("Milestone check-in recorded!");

      setPip(prev => ({
        ...prev,
        pip_milestones: updatedMilestones
      }));

    } catch (error) {
      console.error("Failed to save check-in:", error);
      toast.error(error.response?.data?.message || "Failed to save check-in");
    } finally {
      setSavingCheckIn(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!canApprove("performance_management", true)) return;

    if (!canManagePIP) {
      toast.error("Only the assigned manager can update this PIP");
      return;
    }
    setSubmitting(true);
    try {

      const score = answeredCount > 0
        ? parseFloat((Object.values(starScores).reduce((s, v) => s + v, 0) / Object.values(starScores).length).toFixed(1))
        : 0;

      const responses = sections.flatMap(section =>
        (section.questions || []).map(question => ({
          section_id: section.id,
          section_title: section.title,
          question_id: question.id,
          question_text: question.text,
          rating: starScores[question.id] || 0,
          rating_label: ratings[question.id] || '',
          comment: comments[question.id] || '',
          star_score: starScores[question.id] || 0
        }))
      );

      await savePIP(id, {
        milestones: pip.pip_milestones,
        responses: responses,
        overallComment: '',
        overallRating: score,
        isDraft: true
      });

      toast.success("PIP draft saved successfully!");

      await loadPIP();

    } catch (err) {
      console.error("Failed to save draft:", err);
      toast.error(err.response?.data?.message || "Failed to save draft");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompletePIP = async () => {
    if (!canApprove("performance_management", true)) return;

    if (!canManagePIP) {
      toast.error("Only the assigned manager can complete this PIP");
      return;
    }
    setSubmitting(true);
    try {
      const score = answeredCount > 0
        ? parseFloat((Object.values(starScores).reduce((s, v) => s + v, 0) / Object.values(starScores).length).toFixed(1))
        : 0;

      const responses = sections.flatMap(section =>
        (section.questions || []).map(question => ({
          section_id: section.id,
          section_title: section.title,
          question_id: question.id,
          question_text: question.text,
          rating: starScores[question.id] || 0,
          rating_label: ratings[question.id] || '',
          comment: comments[question.id] || '',
          star_score: starScores[question.id] || 0
        }))
      );

      await savePIP(id, {
        milestones: pip.pip_milestones,
        responses: responses,
        overallComment: '',
        overallRating: score,
        isComplete: true
      });

      toast.success("PIP completed successfully!");
      await loadPIP();

    } catch (err) {
      console.error("Failed to complete PIP:", err);
      toast.error(err.response?.data?.message || "Failed to complete PIP");
    } finally {
      setSubmitting(false);
    }
  };

  const ratingOptions = template?.ratingOptions ? [...template.ratingOptions].reverse() : ["No Improvement", "Partial Progress", "Met Target", "Exceeded Target", "Significantly Exceeded"];
  const sections = template?.sections || [];
  const allQuestionIds = sections.flatMap(s => (s.questions || []).map(q => q.id));
  const answeredCount = Object.keys(ratings).filter(k => allQuestionIds.includes(k) && ratings[k]).length;
  const progressPct = allQuestionIds.length > 0 ? Math.round((answeredCount / allQuestionIds.length) * 100) : 0;
  const canManagePIP = pip?.can_manage === true;

  // Helper function to convert string rating to numeric value based on template's rating options
  const getRatingNumber = (ratingLabel) => {
    if (!ratingLabel) return 0;
    const index = ratingOptions.indexOf(ratingLabel);
    return index >= 0 ? ratingOptions.length - index : 0;
  };

  const getRatingStyle = (option, isSelected) => {
    if (!isSelected) return "border-border bg-card text-muted-foreground hover:border-red-400/40 hover:bg-red-50/30 dark:hover:bg-red-950/10";

    const styleMap = {
      "significantly": "border-emerald-600 bg-emerald-50 text-emerald-800 ring-emerald-600/20 dark:bg-emerald-950/50 dark:text-emerald-200 dark:border-emerald-500",
      "exceeded": "border-emerald-500 bg-emerald-50 text-emerald-700 ring-emerald-500/20 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-600",
      "met": "border-primary bg-primary/10 text-primary ring-primary/20 dark:bg-primary/20",
      "partial": "border-amber-500 bg-amber-50 text-amber-700 ring-amber-500/20 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-600",
      "no improvement": "border-red-500 bg-red-50 text-red-700 ring-red-500/20 dark:bg-red-950/40 dark:text-red-300 dark:border-red-600"
    };

    const lowerOption = option.toLowerCase();
    for (const [key, style] of Object.entries(styleMap)) {
      if (lowerOption.includes(key)) return style;
    }

    return "border-primary bg-primary/10 text-primary ring-primary/20 dark:bg-primary/20";
  };


  if (loading) return <><div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></>;

  // ==================== CREATION FLOW ====================
  if (isNew) {
    const durationDays = startDate && endDate ? Math.round((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) : 0;
    return (
      <>
        <div className="max-w-7xl mx-auto py-8">
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-from-pip-new"><ArrowLeft className="h-5 w-5" /></Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground" data-testid="new-pip-title">Assign Performance Improvement Plan</h1>
              <p className="text-sm text-muted-foreground">Create a structured PIP with scheduled review milestones</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Employee & Reason */}
            <Card>
              <CardHeader className="border-b bg-muted/50 dark:bg-muted/50">
                <CardTitle className="text-base flex items-center gap-2 text-primary"><AlertTriangle className="h-5 w-5" /> PIP Details</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-5">
                {employee ? (
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50 border">
                    <Avatar className="h-12 w-12"><AvatarFallback className="bg-primary/10 text-primary font-bold">{employee.name.split(' ').map(n => n[0]).join('')}</AvatarFallback></Avatar>
                    <div>
                      <p className="font-semibold text-foreground">{employee.name}</p>
                      <p className="text-sm text-muted-foreground">{employee.designation} — {employee.department}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label className="font-medium">Select Employee</Label>
                    <select
                      className="w-full p-2.5 rounded-lg border bg-card text-foreground text-sm"
                      value={selectedEmployeeId}
                      onChange={(e) => {
                        setSelectedEmployeeId(e.target.value);
                        const emp = employees.find(emp =>
                          emp.employee_id === e.target.value || emp._id === e.target.value
                        );
                        setEmployee(emp || null);
                      }}
                      data-testid="pip-employee-select"
                    >
                      <option value="">Choose an employee...</option>
                      {employees.map(e => (
                        <option key={e._id || e.employee_id} value={e.employee_id}>
                          {e.name} — {e.department}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="font-medium">Reason for PIP</Label>
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Describe the performance concerns that necessitate this PIP..." className="min-h-[80px]" data-testid="pip-reason" />
                </div>
              </CardContent>
            </Card>

            {/* Dates & Duration */}
            <Card>
              <CardHeader className="border-b">
                <CardTitle className="text-base flex items-center gap-2"><Calendar className="h-5 w-5 text-primary" /> PIP Timeline</CardTitle>
                <CardDescription>Set the PIP period. Milestones will be auto-generated and are fully editable.</CardDescription>
              </CardHeader>
              <CardContent className="p-6 space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-medium">Start Date</Label>
                    <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="pip-start-date" />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-medium">End Date</Label>
                    <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} min={startDate} data-testid="pip-end-date" />
                  </div>
                </div>
                {durationDays > 0 && (
                  <Badge variant="outline" className="text-sm border-primary/30 text-primary">{durationDays} day PIP period</Badge>
                )}

                {/* Auto-generated Milestones */}
                {milestones.length > 0 && (
                  <div className="space-y-3">
                    <Label className="font-medium">Review Milestones</Label>
                    <p className="text-xs text-muted-foreground">Auto-generated based on PIP duration. Edit names and dates as needed.</p>
                    <div className="space-y-3">
                      {milestones.map((ms, idx) => (
                        <div key={ms.id} className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                          <div className="h-8 w-8 rounded-full bg-primary/10 dark:bg-primary/10 flex items-center justify-center shrink-0">
                            <span className="text-xs font-bold text-primary">{idx + 1}</span>
                          </div>
                          <Input
                            value={ms.label}
                            onChange={(e) => {
                              const updated = [...milestones];
                              updated[idx] = { ...updated[idx], label: e.target.value };
                              setMilestones(updated);
                            }}
                            className="h-8 text-sm flex-1"
                            data-testid={`milestone-label-${idx}`}
                          />
                          <Input
                            type="date"
                            value={ms.date}
                            onChange={(e) => {
                              const updated = [...milestones];
                              updated[idx] = { ...updated[idx], date: e.target.value };
                              setMilestones(updated);
                            }}
                            className="h-8 text-sm w-40"
                            data-testid={`milestone-date-${idx}`}
                          />
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:bg-destructive/10 shrink-0"
                            onClick={() => setMilestones(milestones.filter((_, i) => i !== idx))} disabled={milestones.length <= 1}>
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => setMilestones([...milestones, { id: 'm' + Date.now(), label: '', date: endDate || '', status: 'Pending', notes: '' }])} data-testid="add-milestone-btn">
                      <Plus size={12} /> Add Milestone
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Template Selection */}
            <Card>
              <CardHeader className="border-b">
                <CardTitle className="text-base flex items-center gap-2"><Target className="h-5 w-5 text-primary" /> Evaluation Template</CardTitle>
                <CardDescription>Select the template for PIP evaluation questions.</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                {templates.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No PIP templates found. Create one in the Appraisal Builder first.</p>
                ) : (
                  <div className="grid gap-3">
                    {templates.map((tmpl) => (
                      <div
                        key={tmpl._id || tmpl.id}
                        onClick={() => setSelectedTemplateId(tmpl._id || tmpl.id)}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${selectedTemplateId === (tmpl._id || tmpl.id)
                          ? 'border-primary bg-primary/50 ring-2 ring-primary/10 dark:bg-primary/20 dark:border-primary-600'
                          : 'border-border hover:border-primary/40 hover:bg-muted/50'
                          }`}
                        data-testid={`pip-template-option-${tmpl._id || tmpl.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-foreground">{tmpl.name}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {tmpl.sections?.length || 0} sections · {
                                tmpl.sections?.reduce((s, sec) => s + (sec.questions?.length || 0), 0) || 0
                              } questions
                            </p>
                          </div>
                          <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${selectedTemplateId === (tmpl._id || tmpl.id)
                            ? 'border-primary bg-primary'
                            : 'border-border'
                            }`}>
                            {selectedTemplateId === (tmpl._id || tmpl.id) && (
                              <CheckCircle2 className="h-3.5 w-3.5 text-white" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
              <CardFooter className="border-t p-4 flex justify-end gap-3">
                <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
                <Button className="bg-primary hover:bg-primary/90 text-white gap-2" onClick={handleCreate} disabled={creating || !selectedTemplateId || !selectedEmployeeId || !startDate || !endDate} data-testid="create-pip-btn">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />} Assign PIP
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </>
    );
  }

  // ==================== PIP NOT FOUND ====================
  if (!pip) {
    return (
      <><div className="text-center py-20 text-muted-foreground"><p className="text-lg">PIP record not found.</p><Button variant="outline" className="mt-4" onClick={() => navigate(`${reviewsPath}?tab=pips`)}>Back</Button></div></>
    );
  }

  const isCompleted = pip.status === 'Completed';
  const isCancelled = pip.status === 'Cancelled';
  const isLocked = isCompleted || isCancelled;
  const pipMilestones = pip.pip_milestones || [];
  const completedMilestones = pipMilestones.filter(m => m.completed === true).length;
  const totalMilestones = pipMilestones.length;
  const milestonePct = totalMilestones > 0 ? Math.round
    ((completedMilestones / totalMilestones) * 100) : 0;

  // ==================== PIP EXECUTION ====================
  return (
    <>
      <div className="max-w-7xl mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-from-pip"><ArrowLeft className="h-5 w-5" /></Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-foreground">Performance Improvement Plan</h1>
                <Badge className={isCompleted ? "bg-primary text-white dark:bg-primary-600 dark:text-primary-400" : ""}>{pip.status === 'Draft' ? 'Pending' : pip.status}</Badge>
              </div>
              <p className="text-sm text-muted-foreground">{pip.employee_name} · {new Date(pip.pip_start_date).toLocaleDateString()} to {new Date(pip.pip_end_date).toLocaleDateString()}</p>            </div>
          </div>
          <div className="flex items-center gap-6">
            {!isLocked && canManagePIP && (
              <>
                <Button variant="outline" className="" onClick={handleSaveDraft} disabled={submitting} data-testid="save-draft-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Draft
                </Button>
                <Button
                  className="bg-primary hover:bg-primary/90 text-white"
                  onClick={handleCompletePIPClick}
                  disabled={submitting || !isFormComplete()}
                  data-testid="complete-pip-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Complete PIP
                </Button>
              </>
            )}
          </div>
        </div>

        {isCancelled && (
          <Card className="border-amber-200 bg-red-50/80 dark:border-amber-900 dark:bg-amber-950/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                    This PIP has been cancelled
                  </p>
                  <p className="text-sm text-amber-700/80 dark:text-amber-300/80 mt-1">
                    This record is locked and can no longer be updated.
                  </p>
                  {pip.cancellation_reason && (
                    <p className="text-sm text-amber-700/80 dark:text-amber-300/80 mt-2">
                      <span className="font-medium">Reason : </span> {pip.cancellation_reason}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!isLocked && !canManagePIP && (
          <Card className="border-amber-200 bg-amber-50/70 dark:border-amber-800 dark:bg-amber-950/20">
            <CardContent className="p-4">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                This PIP has an assigned manager. Only that manager can update milestones or complete the PIP.
              </p>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-3">
            <div className="lg:sticky lg:top-0">
              <div className="space-y-4">
                <Card className="border-primary/60 dark:border-primary/60">
                  <CardContent className="pt-6 flex flex-col items-center text-center">
                    <Avatar className="h-20 w-20 mb-3">
                      <AvatarFallback className="text-lg bg-primary/10 text-primary font-bold">
                        {(pip.employee_name || 'Unknown').split(' ').map(n => n[0]).join('').toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <h2 className="font-bold text-lg">{pip.employee_name || 'Unknown Employee'}</h2>
                    <p className="text-sm text-muted-foreground">{employee?.designation || 'N/A'}</p>
                    <div className="mt-4 w-full space-y-2 text-left text-sm">
                      <div className="flex justify-between py-1 border-b gap-2">
                        <span className="text-muted-foreground shrink-0">ID</span>
                        <span className="font-medium text-right truncate max-w-[200px]" title={employee?.employee_id || 'N/A'}>
                          {employee?.employee_id || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between py-1 border-b">
                        <span className="text-muted-foreground">Department</span>
                        <span className="font-medium">{employee?.department || 'N/A'}</span>
                      </div>
                      {pip?.employee_id?.manager && (
                        <div className="flex justify-between py-1 border-b">
                          <span className="text-muted-foreground">Manager</span>
                          <span className="font-medium">{pip.employee_id.manager || '--'}</span>
                        </div>
                      )}
                      {employee?.date_of_joining && (
                        <div className="flex justify-between py-1 border-b">
                          <span className="text-muted-foreground">Joined</span>
                          <span className="font-medium">{new Date(employee.date_of_joining).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {pip.pip_reason && (
                  <Card className="border-primary/60 dark:border-primary/60">
                    <CardContent className="p-4">
                      <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-1">Reason</p>
                      <p className="text-sm text-foreground line-clamp-6 break-words">{pip.pip_reason}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Milestones */}
                {pipMilestones.length > 0 && (
                  <Card className="border-primary/60 dark:border-primary/60" data-testid="pip-milestones-card">
                    <CardHeader className="pb-2">
                      <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-1">Review Milestones</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Progress value={milestonePct} className="h-1.5 flex-1" />
                        <span className="text-xs text-primary">{completedMilestones}/{totalMilestones}</span>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4 pt-0 space-y-2.5">
                      {pipMilestones.map((ms) => {
                        const isDone = ms.completed === true || ms.status === 'Completed';
                        const isPast = new Date(ms.date) < new Date();
                        return (
                          <div
                            key={getMilestoneId(ms)}
                            className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                              isDone ? 'bg-primary/50 border-primary/50 dark:bg-primary/20 dark:border-primary-600' : 'bg-muted/50 border-border/50'
                            } ${!isDone && !isLocked && canManagePIP ? 'cursor-pointer hover:shadow-sm' : ''}`}
                            onClick={() => {
                              if (!isDone && !isLocked && canManagePIP) {
                                setCheckInMilestone({
                                  milestone_id: ms.milestone_id,
                                  id: ms.id,
                                  _id: ms._id,
                                  label: ms.label,
                                  date: ms.date
                                });
                                setCheckInNotes(ms.notes || '');
                                setCheckInRating(ms.rating || '');
                              }
                            }}
                            data-testid={`milestone-${getMilestoneId(ms)}`}
                          >
                            <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${isDone ? 'bg-primary' : isPast ? 'bg-muted' : 'bg-muted'
                              }`}>
                              {isDone ? (
                                <CheckCircle2 className="h-3.5 w-3.5 text-white" />
                              ) : (
                                <Clock className="h-3.5 w-3.5 text-muted-foreground dark:text-primary" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs font-medium truncate ${isDone ? 'text-primary dark:text-primary-300' : 'text-foreground'
                                }`}>
                                {ms.label}
                              </p>
                              <p className="text-[10px] text-muted-foreground">{new Date(ms.date).toLocaleDateString()}</p>
                              {isDone && ms.notes && (
                                <p className="text-[10px] text-primary dark:text-primary-400 mt-1 italic truncate">
                                  "{ms.notes}"
                                </p>
                              )}
                              {!isDone && isPast && (
                                <p className="text-[10px] text-muted-foreground font-medium">Overdue</p>
                              )}
                            </div>
                            {!isDone && !isLocked && canManagePIP && (
                              <Pencil className="h-3 w-3 text-muted-foreground shrink-0 mt-1" />
                            )}
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9 space-y-16">
            {/* Template sections */}
            {sections.map((section, sectionIndex) => {
              const sectionQIds = (section.questions || []).map(q => q.id);
              const sectionAnswered = sectionQIds.filter(qId => ratings[qId]).length;
              const sectionTotal = sectionQIds.length;
              const sectionPct = sectionTotal > 0 ? Math.round((sectionAnswered / sectionTotal) * 100) : 0;
              return (
                <div key={section.id} className="space-y-8" data-testid={`pip-section-${section.id}`}>
                  <div className="relative rounded-xl border dark:border-muted-foreground/20  bg-card overflow-hidden">
                    <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-primary" />
                    <div className="pl-5 pr-5 py-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3.5">
                          <div className="h-9 w-9 rounded-lg bg-primary/10 dark:bg-primary/30 flex items-center justify-center shrink-0 mt-0.5"><span className="text-sm font-bold text-primary dark:text-primary-400">{sectionIndex + 1}</span></div>
                          <div>
                            <h3 className="text-base font-semibold text-foreground leading-tight">{section.title}</h3>
                            {section.description && <p className="text-sm text-muted-foreground mt-1">{section.description}</p>}
                          </div>
                        </div>
                        {section.weight > 0 && <Badge className="bg-primary/10 text-primary border-primary dark:bg-primary/30 dark:text-primary-400 dark:border-primary-600 hover:bg-primary/10 text-xs font-semibold px-3 py-1">{section.weight}% Weight</Badge>}
                      </div>
                      {!isLocked && canManagePIP && <div className="mt-4 flex items-center gap-3"><Progress value={sectionPct} className="h-1.5 flex-1" /><span className="text-xs font-medium text-muted-foreground">{sectionAnswered}/{sectionTotal}</span></div>}
                    </div>
                  </div>
                  {(section.questions || []).map((question, qi) => {
                    const selectedRating = ratings[question.id] || "";
                    return (
                      <div key={question.id} className="question-card p-0" data-testid={`pip-question-${question.id}`}>
                        <div className="flex items-start gap-4 px-6 pt-5 pb-4">
                          <div className="h-8 w-8 rounded-full bg-primary/10 dark:bg-primary/30 flex items-center justify-center shrink-0"><span className="text-xs font-bold text-primary dark:text-primary-400">{qi + 1}</span></div>
                          <div className="flex-1 pt-0.5"><p className="text-[15px] font-medium text-foreground leading-relaxed">{question.text}</p></div>
                          {selectedRating && <Badge variant="outline" className="shrink-0 text-[10px] border-primary text-primary bg-primary/10 dark:border-primary-600 dark:text-primary-400 dark:bg-primary/20"><CheckCircle2 className="h-3 w-3 mr-1" /> Reviewed</Badge>}
                        </div>
                        <Separator />
                        <div className="px-6 py-4">
                          <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 block">Performance Assessment</Label>
                          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2.5">
                            {ratingOptions.map((option) => {
                              const isSelected = selectedRating === option;
                              return (
                                <button key={option} type="button" onClick={() => {
                                  if (!isLocked && canManagePIP) {
                                    setRatings(prev => ({ ...prev, [question.id]: option }));
                                    const optionIndex = ratingOptions.indexOf(option);
                                    const starScore = optionIndex + 1;
                                    setStarScores(prev => ({ ...prev, [question.id]: starScore }));
                                  }
                                }} data-selected={isSelected}
                                  className={`rounded-md border border-muted/60 rating-pill ${getRatingStyle(option, isSelected)} ${isLocked || !canManagePIP ? 'cursor-default' : ''}`} disabled={isLocked || !canManagePIP}>{option}</button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="px-6 pb-4">
                          <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 block">Star Score</Label>
                          <StarRating
                            rating={starScores[question.id] || 0}
                            size={22}
                            interactive={false}
                            onChange={(stars) => {
                              if (canManagePIP) setStarScores(prev => ({ ...prev, [question.id]: stars }));
                            }}
                          />
                        </div>

                        {!isLocked && canManagePIP && (
                          <div className="px-6 pb-5">
                            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">Manager Notes</Label>
                            <Textarea placeholder="Document progress, specific examples..." value={comments[question.id] || ""} onChange={(e) => setComments(prev => ({ ...prev, [question.id]: e.target.value }))} className="resize-none min-h-[70px] bg-card border-border/60 text-sm" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {sections.length === 0 && (
              <Card className="border-muted dark:border-muted"><CardContent className="p-6 text-center"><p className="text-muted-foreground">No PIP template found. Create one in the Builder first.</p><Button variant="outline" className="mt-4" onClick={() => navigate('/settings/appraisal-builder')}>Go to Builder</Button></CardContent></Card>
            )}
          </div>
        </div>
      </div>

      {/* Milestone Check-in Dialog */}
      <Dialog open={!!checkInMilestone} onOpenChange={(open) => { if (!open) setCheckInMilestone(null); }}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Calendar className="h-5 w-5 text-primary" /> Milestone Check-in</DialogTitle>
            <DialogDescription>Record progress and notes for this milestone.</DialogDescription>
          </DialogHeader>
          {checkInMilestone && (
            <div className="space-y-4 py-2">
              <div className="p-3 rounded-lg bg-muted/50 border">
                <p className="font-semibold text-foreground dark:text-white">{checkInMilestone.label}</p>
                <p className="text-xs text-muted-foreground">Scheduled: {new Date(checkInMilestone.date).toLocaleDateString()}</p>
              </div>
              <div className="space-y-2">
                <Label className="font-medium">Progress Rating</Label>
                <div className="grid grid-cols-3 gap-2">
                  {["On Track", "Needs Attention", "Not Improving"].map(r => (
                    <button key={r} type="button" onClick={() => setCheckInRating(r)}
                      className={`p-2.5 rounded-lg border-2 text-xs font-medium transition-all ${checkInRating === r
                        ? r === "On Track" ? "border-primary bg-primary text-primary-700 dark:bg-primary/40 dark:text-primary-400 dark:border-primary-600"
                          : r === "Needs Attention" ? "border-primary bg-primary text-primary-700 dark:bg-primary/40 dark:text-primary-400 dark:border-primary-600"
                            : "border-primary bg-primary text-primary-700 dark:bg-primary/40 dark:text-primary-400 dark:border-primary-600"
                        : "border-border hover:border-primary/40"}`}
                      data-testid={`checkin-rating-${r.replace(/\s+/g, '-').toLowerCase()}`}>{r}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-medium">Check-in Notes</Label>
                <Textarea value={checkInNotes} onChange={(e) => setCheckInNotes(e.target.value)} placeholder="Document the employee's progress, improvements, areas still needing work..." className="min-h-[100px]" data-testid="checkin-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckInMilestone(null)}>Cancel</Button>
            <Button className="bg-primary hover:bg-primary/90 text-white gap-2" onClick={handleMilestoneCheckIn} disabled={savingCheckIn || !canApprove("performance_management")} data-testid="save-checkin-btn">
              {savingCheckIn ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Complete Check-in
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCompleteConfirm} onOpenChange={setShowCompleteConfirm}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-primary" />
              Complete Performance Improvement Plan
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to complete this PIP? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="p-3 rounded-lg bg-primary/50 dark:bg-primary/40 border border-primary/60">
              <p className="text-sm text-foreground dark:text-white">
                <strong>Employee:</strong> {pip.employee_name}
              </p>
              <p className="text-sm text-foreground dark:text-white mt-1">
                <strong>Progress:</strong> {answeredCount}/{allQuestionIds.length} questions rated
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteConfirm(false)}>
              Cancel
            </Button>
            <Button className="bg-primary hover:bg-primary/90 text-white gap-2" onClick={confirmCompletePIP}>
              <CheckCircle2 className="h-4 w-4" /> Confirm & Complete PIP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PIPForm;
